import type { AuthGroup, AuthRole } from '@fourthwall/auth'

import type { DashboardPermissions } from '../types'

export const groupsPermissions: DashboardPermissions<(AuthGroup | AuthRole)[]> = {
  home: {
    analytics: ['OWNER', 'MANAGER'],
    fulfillments: ['OWNER', 'MANAGER'],
    recommendations: ['OWNER', 'MANAGER'],
    guidesAndTips: ['OWNER', 'MANAGER'],
    campaign: ['ROLE_SHOP_WRITE'],
  },
  contributions: {
    profit: ['OWNER', 'MANAGER'],
    orders: {
      view: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT', 'ROLE_ORDER_READ'],
      edit: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT'],
      cancel: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT'],
      refund: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT'],
      redoGiveaway: ['OWNER', 'MANAGER'],
      timelines: ['ROLE_TIMELINE_READ'],
      statistics: ['ROLE_ORDER_READ'],
    },
    donations: ['OWNER', 'MANAGER', 'CS'],
    thankYous: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT', 'ROLE_KUDOS_READ'],
    fulfillments: {
      view: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT', 'ROLE_FULFILLMENT_READ'],
      upload: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT'],
    },
    messages: {
      view: ['OWNER'],
      send: ['OWNER'],
    },
  },
  products: {
    view: ['OWNER', 'MANAGER', 'CS', 'DESIGNER'],
    create: ['OWNER', 'MANAGER', 'DESIGNER'],
    edit: {
      additionalSections: ['OWNER', 'MANAGER', 'DESIGNER'],
      details: ['OWNER', 'MANAGER', 'DESIGNER'],
      status: ['OWNER', 'MANAGER', 'DESIGNER'],
      stock: ['OWNER', 'MANAGER'],
      url: ['OWNER', 'MANAGER', 'DESIGNER'],
    },
    thirdPartyLogistics: {
      view: ['OWNER', 'MANAGER', 'CS', 'FULFILLMENT'],
      edit: ['OWNER', 'MANAGER', 'FULFILLMENT'],
    },
    duplicate: ['OWNER', 'MANAGER', 'DESIGNER'],
    orderSamples: ['OWNER', 'MANAGER', 'DESIGNER'],
    advancedSettings: ['OWNER', 'MANAGER', 'DESIGNER'],
    archive: ['OWNER', 'MANAGER', 'DESIGNER'],
  },
  collections: {
    view: ['OWNER', 'MANAGER', 'CS', 'DESIGNER'],
    create: ['OWNER', 'MANAGER', 'CS', 'DESIGNER'],
    edit: ['OWNER', 'MANAGER', 'CS', 'DESIGNER'],
  },
  promotions: {
    shopPromoCodes: ['OWNER', 'MANAGER'],
    membershipsPromoCodes: ['OWNER', 'MANAGER'],
    giveawayLinks: ['OWNER', 'MANAGER'],
    giftCards: ['OWNER', 'MANAGER'],
  },
  analytics: {
    profit: ['OWNER', 'MANAGER'],
    totalMembers: ['OWNER', 'MANAGER'],
    membersChange: ['OWNER', 'MANAGER'],
    contributions: ['OWNER', 'MANAGER'],
    averageContributionValue: ['OWNER', 'MANAGER'],
    topProducts: ['OWNER', 'MANAGER'],
    salesByCountry: ['OWNER', 'MANAGER'],
    topTiers: ['OWNER', 'MANAGER'],
    paymentMethodUsed: ['OWNER', 'MANAGER'],
    conversionRates: ['OWNER', 'MANAGER'],
    uniqueVisitors: ['OWNER', 'MANAGER'],
    salesByTrafficSource: ['OWNER', 'MANAGER'],
    sessionsByTrafficSource: ['OWNER', 'MANAGER'],
    salesBySocialSource: ['OWNER', 'MANAGER'],
    sessionsBySocialSource: ['OWNER', 'MANAGER'],
  },
  apps: ['OWNER', 'MANAGER'],
  themeEditor: {
    general: ['OWNER', 'MANAGER', 'DESIGNER'],
    layout: ['OWNER', 'MANAGER', 'DESIGNER'],
  },
  memberships: {
    setup: ['OWNER', 'MANAGER'],
    overview: ['OWNER', 'MANAGER'],
    post: {
      view: ['OWNER', 'MANAGER', 'MEMBERSHIPS_MODERATOR'],
      create: ['OWNER', 'MANAGER', 'MEMBERSHIPS_MODERATOR'],
    },
    messages: ['OWNER', 'MANAGER', 'MEMBERSHIPS_MODERATOR'],
    video: {
      view: ['OWNER', 'MANAGER', 'MEMBERSHIPS_MODERATOR'],
      create: ['OWNER', 'MANAGER', 'MEMBERSHIPS_MODERATOR'],
    },
    members: ['OWNER', 'MANAGER'],
    perksAndTiers: {
      createPerk: ['OWNER', 'MANAGER'],
      createTier: ['OWNER', 'MANAGER'],
    },
    settings: {
      general: ['OWNER', 'MANAGER'],
      messages: ['OWNER', 'MANAGER'],
      badges: ['OWNER', 'MANAGER'],
      customEmoji: ['OWNER', 'MANAGER'],
      postTags: ['OWNER', 'MANAGER'],
      welcomePost: ['OWNER', 'MANAGER'],
      mobileApp: ['OWNER', 'MANAGER'],
      upselling: ['OWNER', 'MANAGER'],
    },
  },
  settings: {
    general: ['OWNER', 'MANAGER'],
    team: {
      view: ['OWNER', 'MANAGER'],
      invite: ['OWNER', 'MANAGER'],
      changeRole: ['OWNER', 'MANAGER'],
      remove: ['OWNER'],
    },
    shipping: ['OWNER', 'MANAGER'],
    domain: ['OWNER', 'MANAGER'],
    billing: {
      view: ['OWNER', 'MANAGER'],
      payouts: {
        setup: ['OWNER'],
        edit: ['OWNER'],
      },
      paymentMethods: ['OWNER', 'MANAGER'],
      history: {
        payouts: ['OWNER', 'MANAGER'],
        charges: ['OWNER', 'MANAGER'],
        full: ['OWNER', 'MANAGER'],
      },
    },
    plan: {
      view: ['OWNER', 'MANAGER'],
      storageLimits: ['OWNER', 'MANAGER'],
    },
    samples: {
      view: ['OWNER', 'MANAGER'],
      create: ['OWNER', 'MANAGER'],
    },
    referrals: ['OWNER', 'MANAGER'],
    checkout: {
      general: ['OWNER', 'MANAGER'],
      notifications: ['ROLE_NOTIFICATION_READ'],
    },
    forDevelopers: {
      openAPI: ['OWNER', 'MANAGER'],
      webhooks: ['OWNER', 'MANAGER'],
      headless: ['OWNER', 'MANAGER'],
      hmac: ['OWNER', 'MANAGER'],
    },
    thankYouCards: {
      view: ['OWNER', 'MANAGER'],
      edit: ['OWNER', 'MANAGER'],
    },
    platformApps: ['OWNER'],
  },
  accountSettings: ['OWNER', 'MANAGER'],
  bookACall: ['OWNER', 'MANAGER'],
  creatorChallenge: ['OWNER', 'MANAGER'],
  visitState: ['OWNER', 'MANAGER'],
  subscriptions: {
    view: ['OWNER', 'MANAGER', 'CS', 'DESIGNER', 'FULFILLMENT'],
    edit: ['OWNER', 'MANAGER'],
  },
}
